// AI post-generation dialog — Piece B of the wedge features batch.
//
// Opened from the property detail page. Lets the user pick a tone,
// override the phone number, and ask the AI for a Zalo-ready property
// post in Vietnamese. The generated text lands in an editable preview
// — the user can tweak before copying or sharing.
//
// Layouts:
//   - Desktop (>= 1024px): the original 2-column Dialog. Tracks
//     /Users/datnguyen/Downloads/AI post desktop.html.
//   - Mobile (< 1024px): bottom Sheet sliding up to a partial-height
//     panel. Tracks /Users/datnguyen/Downloads/Ai post mobile.html.
//     The property-detail page stays visible behind a dim overlay so
//     the user keeps spatial context.
//
// What's intentionally NOT shown on either branch:
//   - Hashtags strip (removed earlier per a separate spec change).
//   - Model usage strip ("gpt-4o-mini · 1240ms") — removed per the
//     design handoff transcript.
// What's intentionally NOT shown on mobile only:
//   - The GỢI Ý tip card (saves vertical space; only on desktop).

import { useEffect, useMemo, useState } from "react";
import { Check, RefreshCw, Sparkle, Sparkles, X } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/lib/auth-store";
import { localizeError } from "@/lib/errorMessages";
import { copyToClipboard, shareOrCopy } from "@/lib/share";
import { ZaloShareButton } from "@/features/property/components/zalo-share-button";
import { useGeneratePost, type GeneratePostTone } from "@/features/ai/api";
import { propertyTypeLabel } from "@/features/property/utils/property-enum-options";
import { useMediaQuery } from "@/features/lead/hooks/use-media-query";
import type { Property } from "@/types/property";

interface GeneratePostDialogProps {
  property: Property;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

interface ToneOption {
  value: GeneratePostTone;
  label: string;
  description: string;
}

const TONE_OPTIONS: readonly ToneOption[] = [
  {
    value: "professional",
    label: "Chuyên nghiệp",
    description: "Trang trọng, chính xác, súc tích",
  },
  {
    value: "casual",
    label: "Thân thiện",
    description: "Gần gũi, dễ tiếp cận",
  },
  {
    value: "luxurious",
    label: "Sang trọng",
    description: "Cao cấp, gợi cảm xúc",
  },
] as const;

/** Initial two-letter avatar from a full name (e.g. "Mai Anh Trần" → "MT"). */
function initialsFor(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0 || parts[0] === undefined) return "?";
  if (parts.length === 1) return (parts[0]?.[0] ?? "?").toUpperCase();
  const first = parts[0][0] ?? "";
  const last = parts[parts.length - 1]?.[0] ?? "";
  return `${first}${last}`.toUpperCase();
}

export function GeneratePostDialog({
  property,
  open,
  onOpenChange,
}: GeneratePostDialogProps) {
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  // Re-render with a fresh state tree each time the surface opens: the
  // body lives in a child keyed on the open marker, so React unmounts
  // it on close and mounts a clean copy on open. This avoids stale
  // text from a previous session lingering when the user reopens.
  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        {open ? (
          <DesktopBody
            property={property}
            onClose={() => onOpenChange(false)}
          />
        ) : (
          // Radix needs the Content rendered for the close animation;
          // an empty content during the closed state is sufficient.
          <DialogContent className="sm:max-w-2xl" />
        )}
      </Dialog>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        showCloseButton={false}
        className="max-h-[calc(100vh-60px)] gap-0 rounded-t-[20px] p-0"
      >
        <SheetTitle className="sr-only">Tạo bài đăng AI</SheetTitle>
        {open ? (
          <MobileBody
            property={property}
            onClose={() => onOpenChange(false)}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Shared body state (the mobile + desktop bodies both consume this).
// Keeping it as a hook lets each surface render its own chrome while the
// data/handlers live in one place.
// ---------------------------------------------------------------------------

interface GeneratePostState {
  agentName: string;
  agentPhone: string;
  setAgentPhone: (next: string) => void;
  tone: GeneratePostTone;
  setTone: (next: GeneratePostTone) => void;
  text: string;
  setText: (next: string) => void;
  loading: boolean;
  handleGenerate: () => void;
  handleCopy: () => Promise<void>;
}

function useGeneratePostState(property: Property): GeneratePostState {
  const user = useAuthStore((s) => s.user);
  const [tone, setTone] = useState<GeneratePostTone>("professional");
  const [agentPhone, setAgentPhone] = useState("");
  const [text, setText] = useState("");
  const generate = useGeneratePost();
  const agentName = user?.fullName ?? "—";

  const handleGenerate = () => {
    generate.mutate(
      {
        propertyId: property.id,
        tone,
        ...(agentName.trim().length > 0 && agentName !== "—"
          ? { agentName: agentName.trim() }
          : {}),
        ...(agentPhone.trim().length > 0
          ? { agentPhone: agentPhone.trim() }
          : {}),
      },
      {
        onSuccess: (data) => {
          // Codegen marks `text` as optional; the backend always populates
          // it on success, but the type system needs a fallback.
          setText(data.text ?? "");
        },
        onError: (error: unknown) => {
          toast.error(localizeError(error));
        },
      },
    );
  };

  const handleCopy = async () => {
    if (text.trim().length === 0) return;
    const outcome = await copyToClipboard(text);
    if (outcome.kind === "copied") {
      toast.success("Đã sao chép bài đăng");
    } else {
      toast.error("Không thể sao chép. Vui lòng thử lại.");
    }
  };

  return {
    agentName,
    agentPhone,
    setAgentPhone,
    tone,
    setTone,
    text,
    setText,
    loading: generate.isPending,
    handleGenerate,
    handleCopy,
  };
}

// ---------------------------------------------------------------------------
// Desktop body — original 2-column dialog (tracks AI post desktop.html).
// ---------------------------------------------------------------------------

function DesktopBody({
  property,
  onClose,
}: {
  property: Property;
  onClose: () => void;
}) {
  const state = useGeneratePostState(property);
  const {
    agentName,
    agentPhone,
    setAgentPhone,
    tone,
    setTone,
    text,
    setText,
    loading,
    handleGenerate,
    handleCopy,
  } = state;

  // ⌘↵ / Ctrl+↵ keyboard hint in the footer — Enter alone copies, ⌘↵
  // triggers a Zalo share by clicking the Zalo button programmatically.
  // We don't bind Enter on its own (the user is editing a textarea) —
  // only ⌘/Ctrl+Enter to copy.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        void handleCopy();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // handleCopy + text are stable enough here; React Compiler / lint
    // wants deps but this listener only references closure state on
    // each call so a stale closure copies an earlier `text` value.
    // Re-binding on every text change is fine for a single key listener.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  // Static-ish tip based on the property type — luxurious tone reads
  // best on penthouses / villas / shophouses; otherwise suggest
  // professional. The user is free to ignore.
  const suggestedTone = useMemo<ToneOption>(() => {
    const luxuryTypes: Array<typeof property.propertyType> = [
      "VILLA",
      "SHOPHOUSE",
    ];
    if (
      property.propertyType !== null &&
      luxuryTypes.includes(property.propertyType)
    ) {
      return TONE_OPTIONS[2]!; // luxurious
    }
    return TONE_OPTIONS[0]!; // professional
  }, [property.propertyType]);

  const tipBody = useMemo(() => {
    const typeLabel =
      property.propertyType !== null
        ? propertyTypeLabel(property.propertyType).toLowerCase()
        : "bất động sản";
    return (
      <>
        Dùng tông <b className="font-semibold">{suggestedTone.label}</b> cho{" "}
        {typeLabel} để tăng tỷ lệ tương tác.
      </>
    );
  }, [suggestedTone, property.propertyType]);

  return (
    <DialogContent
      showCloseButton={false}
      className="flex h-[min(680px,90vh)] max-h-[90vh] w-[min(820px,calc(100vw-2rem))] max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-none"
    >
      {/* Header */}
      <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-6 py-4">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <Sparkles aria-hidden="true" className="size-3 text-primary" />
            <span className="font-mono text-[11px] font-bold tracking-[0.06em] text-primary">
              AI POST GENERATION
            </span>
          </div>
          <DialogTitle className="mt-1 text-[20px] leading-tight font-bold tracking-tight">
            Tạo bài đăng AI
          </DialogTitle>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Đóng"
          className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground hover:bg-muted"
        >
          <X aria-hidden="true" className="size-3.5" />
        </button>
      </div>

      {/* Body — 2 columns */}
      <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_1.4fr] overflow-hidden">
        {/* LEFT — controls + tip */}
        <div className="flex min-h-0 flex-col gap-4 overflow-y-auto border-r border-border px-5 py-5">
          {/* Tone — vertical radio cards */}
          <div>
            <p className="mb-2 text-[11px] font-semibold text-ink-soft">
              Tông giọng
            </p>
            <div className="flex flex-col gap-1.5">
              {TONE_OPTIONS.map((opt) => {
                const isActive = opt.value === tone;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setTone(opt.value)}
                    disabled={loading}
                    aria-pressed={isActive}
                    className={cn(
                      "flex items-center gap-2.5 rounded-[10px] border px-3 py-2.5 text-left transition-colors",
                      isActive
                        ? "border-indigo-300 bg-indigo-50"
                        : "border-border bg-background hover:bg-muted/40",
                      loading ? "cursor-not-allowed opacity-60" : "",
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        "flex size-3.5 shrink-0 items-center justify-center rounded-full border-2",
                        isActive
                          ? "border-primary bg-primary"
                          : "border-border-strong bg-transparent",
                      )}
                    >
                      {isActive ? (
                        <span className="size-[5px] rounded-full bg-background" />
                      ) : null}
                    </span>
                    <div className="min-w-0">
                      <div
                        className={cn(
                          "text-[12px] font-semibold",
                          isActive ? "text-primary" : "text-foreground",
                        )}
                      >
                        {opt.label}
                      </div>
                      <div
                        className={cn(
                          "mt-0.5 text-[11px] leading-snug",
                          isActive ? "text-primary/75" : "text-muted-foreground",
                        )}
                      >
                        {opt.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Broker — display card pulled from auth user */}
          <div>
            <p className="mb-2 text-[11px] font-semibold text-ink-soft">
              Môi giới
            </p>
            <div className="flex items-center gap-2 rounded-[9px] border border-border bg-background px-3 py-2 text-[14px]">
              <span
                aria-hidden="true"
                className="flex size-6 shrink-0 items-center justify-center rounded-[7px] bg-gradient-to-br from-primary to-foreground font-mono text-[11px] font-semibold text-primary-foreground"
              >
                {agentName === "—" ? "?" : initialsFor(agentName)}
              </span>
              <span className="min-w-0 truncate">{agentName}</span>
            </div>
          </div>

          {/* Phone */}
          <div>
            <p className="mb-2 text-[11px] font-semibold text-ink-soft">
              Số điện thoại
            </p>
            <input
              type="tel"
              inputMode="tel"
              value={agentPhone}
              onChange={(e) => setAgentPhone(e.target.value)}
              placeholder="0938 111 222"
              disabled={loading}
              className="w-full rounded-[9px] border border-border bg-background px-3 py-2 font-mono text-[14px] outline-none transition-colors focus:border-primary focus:ring-[3px] focus:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>

          {/* Tip — mt-auto pushes to bottom of column when there's room */}
          <div className="mt-auto rounded-[10px] border border-border bg-background p-3">
            <p className="font-mono text-[11px] font-semibold tracking-[0.05em] text-ink-faint">
              GỢI Ý
            </p>
            <p className="mt-1 text-[12px] leading-relaxed text-ink-soft">
              {tipBody}
            </p>
          </div>
        </div>

        {/* RIGHT — generated post */}
        <div className="flex min-h-0 flex-col gap-3 overflow-hidden px-6 py-5">
          {/* Section header */}
          <div className="flex shrink-0 items-center justify-between">
            <span className="text-[11px] font-semibold text-ink-soft">
              Bài đăng
            </span>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={loading}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw
                aria-hidden="true"
                className={cn("size-3", loading ? "animate-spin" : "")}
              />
              {text.length === 0 ? "Tạo bài đăng" : "Tạo lại"}
            </button>
          </div>

          {/* Generated post preview — editable textarea styled like the
              design's display card. */}
          {loading ? (
            <div className="flex min-h-[260px] flex-1 flex-col gap-2 rounded-xl border border-border bg-background p-4">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="mt-1 h-3 w-full" />
              <Skeleton className="h-3 w-11/12" />
              <Skeleton className="h-3 w-4/5" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          ) : (
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Bấm 'Tạo bài đăng' để AI sinh nội dung..."
              className="min-h-[260px] flex-1 resize-none rounded-xl border border-border bg-background px-4 py-3.5 text-[14px] leading-[1.6] text-ink-soft outline-none transition-colors focus:border-primary focus:ring-[3px] focus:ring-ring"
            />
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border bg-muted/30 px-6 py-3">
        <span className="font-mono text-[11px] text-muted-foreground">
          ⌘↵ để sao chép
        </span>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCopy}
            disabled={loading || text.trim().length === 0}
          >
            <Check aria-hidden="true" className="size-3.5" />
            Sao chép
          </Button>
          <ZaloShareButton
            property={property}
            textOverride={text.trim().length > 0 ? text : undefined}
            {...(agentName !== "—" && agentName.trim().length > 0
              ? { agentName: agentName.trim() }
              : {})}
            {...(agentPhone.trim().length > 0
              ? { agentPhone: agentPhone.trim() }
              : {})}
            variant="default"
          />
        </div>
      </div>
    </DialogContent>
  );
}

// ---------------------------------------------------------------------------
// Mobile body — bottom Sheet with a horizontal 3-pill tone control,
// 2-col Môi giới/Số điện thoại display cards, editable textarea, and a
// 3-button footer (regenerate icon, Sao chép, Chia sẻ Zalo).
// Tracks /Users/datnguyen/Downloads/Ai post mobile.html.
// ---------------------------------------------------------------------------

function MobileBody({
  property,
  onClose,
}: {
  property: Property;
  onClose: () => void;
}) {
  const state = useGeneratePostState(property);
  const {
    agentName,
    agentPhone,
    setAgentPhone,
    tone,
    setTone,
    text,
    setText,
    loading,
    handleGenerate,
    handleCopy,
  } = state;

  // Zalo share on mobile — calls the same `shareOrCopy` helper directly so
  // we can render the design's custom primary button (with the `Z` glyph)
  // instead of the canned `<ZaloShareButton>` which wires its own label.
  const user = useAuthStore((s) => s.user);
  const [zaloBusy, setZaloBusy] = useState(false);
  const handleZalo = async () => {
    if (zaloBusy) return;
    setZaloBusy(true);
    try {
      const shareText =
        text.trim().length > 0
          ? text
          : // Fallback summary so the share button works even before generate.
            `${property.buildingName ?? "Bất động sản"}\n` +
              `Liên hệ ${user?.fullName ?? ""} ${agentPhone}`.trim();
      const outcome = await shareOrCopy({
        title: property.buildingName ?? "Bất động sản",
        text: shareText,
      });
      switch (outcome.kind) {
        case "shared":
          toast.success("Đã mở hộp thoại chia sẻ");
          break;
        case "copied":
          toast.success("Đã sao chép. Mở Zalo và dán vào hội thoại.");
          break;
        case "cancelled":
          break;
        case "error":
          toast.error("Không thể chia sẻ. Vui lòng thử lại.");
          break;
      }
    } finally {
      setZaloBusy(false);
    }
  };

  return (
    <div className="flex max-h-[calc(100vh-60px)] min-h-0 flex-col">
      {/* Drag handle */}
      <div className="mx-auto mt-2.5 h-1 w-9 shrink-0 rounded-full bg-border-strong" />

      {/* Header */}
      <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-[18px] pt-3 pb-3.5">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <Sparkles aria-hidden="true" className="size-3 text-primary" />
            <span className="font-mono text-[11px] font-bold tracking-[0.06em] text-primary">
              AI POST
            </span>
          </div>
          <div className="mt-0.5 text-[20px] leading-tight font-bold tracking-tight">
            Tạo bài đăng
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Đóng"
          className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground hover:bg-muted"
        >
          <X aria-hidden="true" className="size-3.5" />
        </button>
      </div>

      {/* Body — scrollable */}
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-[18px] py-4">
        {/* Tone — horizontal 3-pill segmented */}
        <div>
          <p className="mb-2 text-[11px] font-semibold text-ink-soft">
            Tông giọng
          </p>
          <div className="flex w-full gap-1.5">
            {TONE_OPTIONS.map((opt) => {
              const isActive = opt.value === tone;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTone(opt.value)}
                  disabled={loading}
                  aria-pressed={isActive}
                  className={cn(
                    "flex-1 rounded-[10px] border px-2 py-2 text-center text-[12px] font-semibold transition-colors",
                    isActive
                      ? "border-foreground bg-foreground text-background"
                      : "border-border bg-card text-ink-soft",
                    loading ? "cursor-not-allowed opacity-60" : "",
                  )}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Broker + phone — 2-col display cards */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="mb-1.5 text-[11px] font-semibold text-ink-soft">
              Môi giới
            </p>
            <div className="flex items-center gap-2 rounded-[9px] border border-border bg-card px-3 py-2 text-[14px]">
              <span
                aria-hidden="true"
                className="flex size-6 shrink-0 items-center justify-center rounded-[7px] bg-gradient-to-br from-primary to-foreground font-mono text-[11px] font-semibold text-primary-foreground"
              >
                {agentName === "—" ? "?" : initialsFor(agentName)}
              </span>
              <span className="min-w-0 truncate">{agentName}</span>
            </div>
          </div>
          <div>
            <p className="mb-1.5 text-[11px] font-semibold text-ink-soft">
              Số điện thoại
            </p>
            <input
              type="tel"
              inputMode="tel"
              value={agentPhone}
              onChange={(e) => setAgentPhone(e.target.value)}
              placeholder="0938 111 222"
              disabled={loading}
              className="w-full rounded-[9px] border border-border bg-card px-3 py-2 font-mono text-[14px] outline-none transition-colors focus:border-primary focus:ring-[3px] focus:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>
        </div>

        {/* Generated post preview */}
        <div className="flex min-h-0 flex-1 flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-ink-soft">
              Bài đăng
            </span>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={loading}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw
                aria-hidden="true"
                className={cn("size-3", loading ? "animate-spin" : "")}
              />
              {text.length === 0 ? "Tạo bài đăng" : "Tạo lại"}
            </button>
          </div>
          {loading ? (
            <div className="flex min-h-[180px] flex-1 flex-col gap-2 rounded-xl border border-border bg-card p-4">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="mt-1 h-3 w-full" />
              <Skeleton className="h-3 w-11/12" />
              <Skeleton className="h-3 w-4/5" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          ) : (
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Bấm 'Tạo bài đăng' để AI sinh nội dung..."
              className="min-h-[180px] flex-1 resize-none rounded-xl border border-border bg-card px-3.5 py-3 text-[14px] leading-[1.6] text-ink-soft outline-none transition-colors focus:border-primary focus:ring-[3px] focus:ring-ring"
            />
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex shrink-0 items-center gap-2 border-t border-border bg-card px-[18px] pt-3 pb-[22px]">
        <button
          type="button"
          onClick={handleGenerate}
          disabled={loading}
          aria-label="Tạo lại bài đăng"
          className="flex size-11 shrink-0 items-center justify-center rounded-xl border border-border bg-background hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Sparkle aria-hidden="true" className="size-4" />
        </button>
        <Button
          type="button"
          variant="outline"
          onClick={handleCopy}
          disabled={loading || text.trim().length === 0}
          className="h-11 flex-1 rounded-xl"
        >
          <Check aria-hidden="true" className="size-4" />
          Sao chép
        </Button>
        <button
          type="button"
          onClick={handleZalo}
          disabled={zaloBusy}
          className="inline-flex h-11 flex-[1.2] items-center justify-center gap-1.5 rounded-xl bg-zalo px-3 text-[14px] font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span
            aria-hidden="true"
            className="font-mono text-[14px] font-bold"
          >
            Z
          </span>
          Chia sẻ Zalo
        </button>
      </div>
    </div>
  );
}
